/**
 * The finance system's ledger, read into the figures the executive desks use.
 *
 * The platform holds no ledger and should not. What it holds is what work
 * cost, what deferral costs and what the capital projects have spent; revenue,
 * operating cost and the plan live in the airport's finance system. Until now
 * those were typed into a form, which is honest but does not scale past one
 * period and does not survive a month-end.
 *
 * So: a monthly export, read here. A trial balance, a management pack or a
 * revenue report, as CSV or a spreadsheet's first sheet. Three rules hold
 * throughout, and they are the whole of the design:
 *
 *   - **Nothing is guessed.** An account this file cannot place is reported
 *     as unmapped with its amount, never dropped and never swept into
 *     "other". A ledger that silently loses a line is worse than no ledger,
 *     because the total still looks plausible.
 *   - **Every mapping says why.** Each row carries the rule that placed it,
 *     so a finance officer can see that "Landing fees" became aeronautical
 *     revenue because the account name matched, and correct it if wrong.
 *   - **The arithmetic is checked against the file.** Where the export
 *     carries its own totals, they are compared with the sum of what was
 *     mapped, and any difference is stated rather than reconciled away.
 *
 * What comes out is a `Finance` with `basis: "ledger"` and the file's
 * provenance attached, so every tile that rests on it can say where it came
 * from — the same contract the entered figures already keep.
 */
import type { CapexLine, Finance, FinanceLine, MonthLine } from "./finance";
import { t } from "../i18n";

export interface LedgerRow {
  /** the account code, where the export carries one */
  code?: string;
  /** what the finance system calls it */
  label: string;
  /** YYYY-MM, where the row is a month */
  month?: string;
  actual: number;
  budget?: number;
  /** the line of the file it came from, so a problem can be pointed at */
  line: number;
}

export interface LedgerRead {
  rows: LedgerRow[];
  /** the header names as the file wrote them, in order */
  headers: string[];
  /** which header filled each field, so the reader can be corrected */
  columns: { code?: string; label?: string; month?: string; actual?: string; budget?: string };
  /** lines the reader could not parse at all, by number and why */
  rejected: { line: number; text: string; why: string }[];
}

export type Target =
  | { kind: "revenue"; key: string; label: string }
  | { kind: "opex"; key: string; label: string }
  | { kind: "capex" }
  | { kind: "passengers" }
  | { kind: "total"; of: "revenue" | "opex" };

export interface Mapped extends LedgerRow {
  target: Target;
  /** the rule that placed it, in words */
  why: string;
}

export interface Unmapped extends LedgerRow {
  /** why nothing claimed it */
  why: string;
}

export interface Reconciliation {
  /** the file's own revenue total, where it carries one */
  fileRevenue: number | null;
  mappedRevenue: number;
  fileOpex: number | null;
  mappedOpex: number;
  /** what is in the file and in nothing else */
  unmappedTotal: number;
  /** true when every total the file states matches what was mapped */
  balanced: boolean;
  lines: string[];
}

export interface LedgerImport {
  finance: Finance;
  mapped: Mapped[];
  unmapped: Unmapped[];
  reconciliation: Reconciliation;
  read: LedgerRead;
  /** the months the file covers, in order */
  months: string[];
}

/* ── reading the file ────────────────────────────────────────────────────── */

const num = (v: string): number | null => {
  if (v == null) return null;
  /* a ledger writes a negative as (1,234) and a currency with separators */
  const t = v.trim().replace(/[^\d.()-]/g, "");
  if (!t || t === "-" || t === "(" || t === ")") return null;
  const neg = /^\(.*\)$/.test(t);
  const n = Number(t.replace(/[()]/g, ""));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
};

/** Split a CSV line, honouring quotes. Ledger exports quote any label with a comma in it. */
export function splitRow(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === "," || c === "\t" || c === ";") {
      out.push(cur);
      cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const HEADER: Record<keyof LedgerRead["columns"], RegExp> = {
  code: /^(account|account ?code|code|gl|nominal|a\/c)$/i,
  label: /^(description|account ?name|name|label|line|narrative|detail)$/i,
  month: /^(month|period|posting ?period|date)$/i,
  actual: /^(actual|amount|value|ytd|year ?to ?date|total|debit|net)$/i,
  budget: /^(budget|plan|planned|forecast|budget ?ytd)$/i,
};

/** A month, from whatever the finance system writes: 2026-03, Mar-26, 03/2026, March 2026. */
export function readMonth(v: string): string | undefined {
  const t = (v || "").trim();
  let m = /^(\d{4})[-/](\d{1,2})$/.exec(t);
  if (m) return `${m[1]}-${String(Number(m[2])).padStart(2, "0")}`;
  m = /^(\d{1,2})[-/](\d{4})$/.exec(t);
  if (m) return `${m[2]}-${String(Number(m[1])).padStart(2, "0")}`;
  const names = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  m = /^([a-z]{3,9})[\s-]*(\d{2,4})$/i.exec(t);
  if (m) {
    const i = names.indexOf(m[1].slice(0, 3).toLowerCase());
    if (i >= 0) {
      const y = m[2].length === 2 ? 2000 + Number(m[2]) : Number(m[2]);
      return `${y}-${String(i + 1).padStart(2, "0")}`;
    }
  }
  return undefined;
}

/**
 * Read the text of an export into rows.
 *
 * The header is found rather than assumed to be the first line: management
 * packs open with a title, a date and a blank line as often as not. The first
 * line that names at least an amount column and something to call it is the
 * header, and everything above it is ignored.
 */
export function readLedger(text: string): LedgerRead {
  const lines = text.split(/\r?\n/);
  let headerAt = -1;
  let headers: string[] = [];
  const columns: LedgerRead["columns"] = {};
  for (let i = 0; i < Math.min(lines.length, 40); i++) {
    const cells = splitRow(lines[i]);
    if (cells.length < 2) continue;
    const found: LedgerRead["columns"] = {};
    for (const cell of cells)
      for (const [field, rx] of Object.entries(HEADER) as [keyof LedgerRead["columns"], RegExp][])
        if (!found[field] && rx.test(cell)) found[field] = cell;
    if (found.actual && (found.label || found.code)) {
      headerAt = i;
      headers = cells;
      Object.assign(columns, found);
      break;
    }
  }
  if (headerAt < 0)
    return {
      rows: [],
      headers: [],
      columns: {},
      rejected: [
        {
          line: 1,
          text: lines[0] ?? "",
          why: t("no header row naming an amount and an account was found in the first forty lines"),
        },
      ],
    };

  const at = (cells: string[], name?: string) => (name ? cells[headers.indexOf(name)] : undefined);
  const rows: LedgerRow[] = [];
  const rejected: LedgerRead["rejected"] = [];
  for (let i = headerAt + 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const cells = splitRow(raw);
    const label = (at(cells, columns.label) ?? "").trim();
    const code = (at(cells, columns.code) ?? "").trim();
    if (!label && !code) continue; /* a spacer row, not a failure */
    const actual = num(at(cells, columns.actual) ?? "");
    if (actual == null) {
      rejected.push({
        line: i + 1,
        text: raw.slice(0, 120),
        why: t("no amount could be read from the {column} column", { column: String(columns.actual) }),
      });
      continue;
    }
    const budget = num(at(cells, columns.budget) ?? "");
    rows.push({
      code: code || undefined,
      label: label || code,
      month: readMonth(at(cells, columns.month) ?? ""),
      actual,
      budget: budget ?? undefined,
      line: i + 1,
    });
  }
  return { rows, headers, columns, rejected };
}

/* ── placing each line ───────────────────────────────────────────────────── */

interface Rule {
  rx: RegExp;
  target: Target;
  why: string;
}

/**
 * The rules, in order, first match wins.
 *
 * Deliberately readable rather than clever. A finance officer has to be able
 * to look at this list and say whether their chart of accounts will land in
 * the right places, and a scoring model they cannot read is a model they will
 * not trust with the board's numbers.
 */
export const RULES: Rule[] = [
  {
    rx: /\btotal revenue|revenue total|total income\b/i,
    target: { kind: "total", of: "revenue" },
    why: "the file's own revenue total",
  },
  {
    rx: /\btotal (operating )?(cost|expenditure|expense)|opex total\b/i,
    target: { kind: "total", of: "opex" },
    why: "the file's own operating cost total",
  },
  {
    rx: /landing|aircraft parking|air ?bridge|aeronautical|passenger service charge|psc\b|security charge|navigation/i,
    target: { kind: "revenue", key: "aeronautical", label: "Aeronautical" },
    why: "an aeronautical charge",
  },
  {
    rx: /retail|duty ?free|shop|f ?& ?b|food|beverage/i,
    target: { kind: "revenue", key: "retail", label: "Retail" },
    why: "retail income",
  },
  { rx: /car ?park|parking/i, target: { kind: "revenue", key: "parking", label: "Parking" }, why: "parking income" },
  {
    rx: /concession|lease|rent|property/i,
    target: { kind: "revenue", key: "concessions", label: "Concessions" },
    why: "a concession or lease",
  },
  {
    rx: /advertis|media/i,
    target: { kind: "revenue", key: "advertising", label: "Advertising" },
    why: "advertising income",
  },
  { rx: /capital|capex|project|wip\b|work in progress/i, target: { kind: "capex" }, why: "a capital line" },
  {
    rx: /staff|salar|payroll|wage|personnel|employee/i,
    target: { kind: "opex", key: "staff", label: "Staff" },
    why: "a staff cost",
  },
  {
    rx: /maintenance|repair|contract|spare|material/i,
    target: { kind: "opex", key: "maintenance", label: "Maintenance and contracts" },
    why: "maintenance or contracts",
  },
  {
    rx: /energy|electric|power|water|fuel|utilit|cooling/i,
    target: { kind: "opex", key: "energy", label: "Energy and utilities" },
    why: "energy or utilities",
  },
  {
    rx: /security|cleaning|service contract|handling/i,
    target: { kind: "opex", key: "security", label: "Security and services" },
    why: "security or services",
  },
  {
    rx: /insurance|licence|license|professional|legal|admin|travel|training|other (cost|expense)/i,
    target: { kind: "opex", key: "other", label: "Other" },
    why: "an other operating cost",
  },
  /* Last, and the order is load-bearing. "Passenger service charge" is
     revenue, not a headcount, and a passenger rule placed above the
     aeronautical one swallowed it — nearly thirty million riyals left the
     revenue total and turned up as passengers. Money is classified before
     people. */
  { rx: /\bpassengers?\b|\bpax\b/i, target: { kind: "passengers" }, why: "a passenger count, not money" },
];

export function place(row: LedgerRow): { target: Target; why: string } | null {
  const text = `${row.code ?? ""} ${row.label}`;
  for (const r of RULES) if (r.rx.test(text)) return { target: r.target, why: r.why };
  return null;
}

/* ── the import ──────────────────────────────────────────────────────────── */

const sum = (xs: { actual: number }[]) => xs.reduce((t, x) => t + x.actual, 0);
const sumBudget = (xs: LedgerRow[]) => xs.reduce((t, x) => t + (x.budget ?? 0), 0);

/**
 * Turn a read file into the figures the desks use.
 *
 * `current` is what the desks read today; anything the file does not carry is
 * kept from it rather than zeroed. A revenue export with no capital lines
 * should not silently empty the capital table.
 */
export function toFinance(
  read: LedgerRead,
  current: Finance,
  meta: { file: string; by: string; at: string; currency?: string },
): LedgerImport {
  const mapped: Mapped[] = [];
  const unmapped: Unmapped[] = [];
  for (const row of read.rows) {
    const p = place(row);
    if (p) mapped.push({ ...row, target: p.target, why: p.why });
    else unmapped.push({ ...row, why: t("no rule matched the account name; it is counted nowhere") });
  }

  const ofKind = <K extends Target["kind"]>(k: K) => mapped.filter((m) => m.target.kind === k);
  const revenueRows = ofKind("revenue");
  const opexRows = ofKind("opex");
  const capexRows = ofKind("capex");
  const paxRows = ofKind("passengers");
  const totals = ofKind("total");

  /* group by the line each rule named, summing the accounts that landed there */
  const group = (rows: Mapped[]): FinanceLine[] => {
    const by = new Map<string, FinanceLine>();
    for (const r of rows) {
      const t = r.target as { key: string; label: string };
      const cur = by.get(t.key) ?? { key: t.key, label: t.label, actual: 0, budget: 0 };
      cur.actual += r.actual;
      cur.budget += r.budget ?? 0;
      by.set(t.key, cur);
    }
    return [...by.values()];
  };

  const revenue = group(revenueRows);
  const opexByArea = group(opexRows);
  const capex: CapexLine[] = capexRows.map((r, i) => ({
    id: r.code || `L-${String(i + 1).padStart(2, "0")}`,
    name: r.label,
    budget: r.budget ?? r.actual,
    spent: r.actual,
    pctComplete: r.budget ? Math.min(100, Math.round((100 * r.actual) / r.budget)) : 0,
  }));

  /* the monthly series, where the file carries months */
  const months = [...new Set(read.rows.map((r) => r.month).filter((m): m is string => !!m))].sort();
  const monthly: MonthLine[] = months.map((m) => {
    const rev = revenueRows.filter((r) => r.month === m);
    const cost = opexRows.filter((r) => r.month === m);
    return { month: m, revenue: sum(rev), cost: sum(cost), budget: sumBudget(rev) };
  });

  const mappedRevenue = sum(revenueRows);
  const mappedOpex = sum(opexRows);
  const fileRevenue = totals.find((t) => (t.target as { of: string }).of === "revenue")?.actual ?? null;
  const fileOpex = totals.find((t) => (t.target as { of: string }).of === "opex")?.actual ?? null;
  const near = (a: number, b: number) => Math.abs(a - b) <= Math.max(1, Math.abs(b) * 0.005);
  const lines: string[] = [];
  if (fileRevenue != null)
    lines.push(
      near(mappedRevenue, fileRevenue)
        ? t("Revenue agrees with the file's own total.")
        : t("Revenue is {amount} away from the file's own total — some accounts did not map.", {
            amount: Math.round(mappedRevenue - fileRevenue).toLocaleString(),
          }),
    );
  else lines.push(t("The file states no revenue total, so there is nothing to check the revenue against."));
  if (fileOpex != null)
    lines.push(
      near(mappedOpex, fileOpex)
        ? t("Operating cost agrees with the file's own total.")
        : t("Operating cost is {amount} away from the file's own total.", {
            amount: Math.round(mappedOpex - fileOpex).toLocaleString(),
          }),
    );
  else lines.push(t("The file states no operating cost total."));
  if (unmapped.length)
    lines.push(
      t("{n} account(s) worth {amount} matched no rule and are counted nowhere.", {
        n: unmapped.length,
        amount: Math.round(sum(unmapped)).toLocaleString(),
      }),
    );
  if (read.rejected.length) lines.push(t("{n} line(s) could not be read at all.", { n: read.rejected.length }));

  const finance: Finance = {
    ...current,
    currency: meta.currency ?? current.currency,
    period: months.length ? `${months[0]} to ${months[months.length - 1]}, from the ledger` : current.period,
    passengers: paxRows.length ? sum(paxRows) : current.passengers,
    revenue: revenue.length ? revenue : current.revenue,
    opex: {
      key: "opex",
      label: "Operating cost",
      actual: mappedOpex || current.opex.actual,
      budget: sumBudget(opexRows) || current.opex.budget,
    },
    opexByArea: opexByArea.length ? opexByArea : current.opexByArea,
    capex: capex.length ? capex : current.capex,
    monthly: monthly.length ? monthly : current.monthly,
    basis: "ledger",
    ledger: {
      file: meta.file,
      importedBy: meta.by,
      importedAt: meta.at,
      rows: read.rows.length,
      mapped: mapped.length,
      unmapped: unmapped.length,
      rejected: read.rejected.length,
      period: months.length ? `${months[0]} to ${months[months.length - 1]}` : "not stated in the file",
    },
  };

  return {
    finance,
    mapped,
    unmapped,
    reconciliation: {
      fileRevenue,
      mappedRevenue,
      fileOpex,
      mappedOpex,
      unmappedTotal: sum(unmapped),
      balanced:
        (fileRevenue == null || near(mappedRevenue, fileRevenue)) &&
        (fileOpex == null || near(mappedOpex, fileOpex)) &&
        unmapped.length === 0,
      lines,
    },
    read,
    months,
  };
}
